import Dashboard from "../../layout/Dashboard";
import { Content, Heading } from "../../styles/OverviewStyle";
import { RFMain } from "../../styles/RatesFeesStyle";
import { ListHead, ListRow } from "../../styles/CrypoAssetStyle";
import { Icon } from '@iconify/react';
import { useEffect, useState } from 'react';
import { withdrawalLimitService } from '../../services/WithdrawalLimitService';
import type { WithdrawalLimit } from '../../../types/ApiTypes';
import { formatWithCommas } from '@/utils/numberHelpers';
import { TabNavigator } from './components/Navigator';
import { toast } from 'react-toastify';

export default function WithdrawalLimits() {
   const [limits, setLimits] = useState<WithdrawalLimit[]>([]);
   const [loading, setLoading] = useState(true);
   const [showForm, setShowForm] = useState(false);
   const [editingId, setEditingId] = useState<string | null>(null);
   const [form, setForm] = useState({
      dailyLimit: '',
      weeklyLimit: '',
      monthlyLimit: ''
   });

   useEffect(() => {
      loadLimits();
   }, []);

   const loadLimits = async () => {
      try {
         const response = await withdrawalLimitService.getAllLimits();
         if (response.status === 'success' && response.data) {
            setLimits(response.data);
         }
      } catch (error) {
         console.error('Failed to load withdrawal limits:', error);
      } finally {
         setLoading(false);
      }
   };

   const handleEdit = (limit: WithdrawalLimit) => {
      setEditingId(limit.id);
      setForm({
         dailyLimit: limit.dailyLimit,
         weeklyLimit: limit.weeklyLimit,
         monthlyLimit: limit.monthlyLimit
      });
      setShowForm(true);
   };

   const handleCancel = () => {
      setShowForm(false);
      setEditingId(null);
      setForm({ dailyLimit: '', weeklyLimit: '', monthlyLimit: '' });
   };

   const handleSubmit = async () => {
      try {
         const data = {
            dailyLimit: parseFloat(form.dailyLimit),
            weeklyLimit: parseFloat(form.weeklyLimit),
            monthlyLimit: parseFloat(form.monthlyLimit)
         };
         
         if (editingId) {
            await withdrawalLimitService.updateLimit(data);
            toast.success('Withdrawal limit updated successfully');
         } else {
            await withdrawalLimitService.createLimit(data);
            toast.success('Withdrawal limit created successfully');
         }
         
         await loadLimits();
         handleCancel();
      } catch (error) {
         console.error('Failed to save withdrawal limit:', error);
         toast.error('Failed to save withdrawal limit');
      }
   };

   return (
      <Dashboard title="Rates & Fees / Withdrawal Limits" icon="solar:tag-price-linear" active="rates_fees">
         <Content>
            <Heading>Rates & Fees</Heading>

            <TabNavigator active="withdrawal_limits" />

            <RFMain>
               <div>
                  <div className="flex justify-between items-center mb-4">
                     <h3 className="text-base font-medium text-[#101018]">Manage Withdrawal Limits</h3>
                     {!showForm && (
                        <button
                           onClick={() => setShowForm(true)}
                           className="px-4 py-2 bg-[#AF7AD5] text-white text-sm rounded-md hover:bg-[#9B5FC4] transition-colors"
                        >
                           Set New Limit
                        </button>
                     )}
                  </div>

                  {showForm && (
                     <div className="mb-6 p-4 border border-[#E7E7E9] rounded-lg bg-[#FAFAFA]">
                        <h4 className="text-sm font-medium text-[#101018] mb-3">{editingId ? 'Edit Withdrawal Limit' : 'Set New Withdrawal Limit'}</h4>
                        <div className="grid grid-cols-3 gap-3">
                           <div>
                              <label className="block text-xs text-[#6D6D72] mb-1">Daily Limit (₦)</label>
                              <input
                                 placeholder="50000"
                                 value={form.dailyLimit}
                                 onChange={e => setForm(s => ({ ...s, dailyLimit: e.target.value }))}
                                 className="w-full h-9 px-3 rounded-md border border-[#E7E7E9] bg-white text-sm text-[#101018] outline-none focus:border-[#AF7AD5]"
                              />
                           </div>
                           <div>
                              <label className="block text-xs text-[#6D6D72] mb-1">Weekly Limit (₦)</label>
                              <input
                                 placeholder="200000"
                                 value={form.weeklyLimit}
                                 onChange={e => setForm(s => ({ ...s, weeklyLimit: e.target.value }))}
                                 className="w-full h-9 px-3 rounded-md border border-[#E7E7E9] bg-white text-sm text-[#101018] outline-none focus:border-[#AF7AD5]"
                              />
                           </div>
                           <div>
                              <label className="block text-xs text-[#6D6D72] mb-1">Monthly Limit (₦)</label>
                              <input
                                 placeholder="500000"
                                 value={form.monthlyLimit}
                                 onChange={e => setForm(s => ({ ...s, monthlyLimit: e.target.value }))}
                                 className="w-full h-9 px-3 rounded-md border border-[#E7E7E9] bg-white text-sm text-[#101018] outline-none focus:border-[#AF7AD5]"
                              />
                           </div>
                        </div>
                        <div className="mt-3 flex gap-2">
                           <button
                              onClick={handleSubmit}
                              className="px-4 py-2 bg-[#AF7AD5] text-white text-sm rounded-md hover:bg-[#9B5FC4] transition-colors"
                           >
                              {editingId ? 'Update Limit' : 'Create Limit'}
                           </button>
                           <button
                              onClick={handleCancel}
                              className="px-4 py-2 bg-gray-200 text-gray-700 text-sm rounded-md hover:bg-gray-300 transition-colors"
                           >
                              Cancel
                           </button>
                        </div>
                     </div>
                  )}

                  {loading ? (
                     <div className="flex justify-center py-8">
                        <Icon icon="svg-spinners:ring-resize" width={32} className="text-[#AF7AD5]" />
                     </div>
                  ) : (
                     <>
                        <ListHead style={{ gridTemplateColumns: '180px 180px 180px 120px 180px 180px 100px', columnGap: '15px' }}>
                           <div>Daily Limit</div>
                           <div>Weekly Limit</div>
                           <div>Monthly Limit</div>
                           <div>Status</div>
                           <div>Created At</div>
                           <div>Updated At</div>
                           <div>Actions</div>
                        </ListHead>

                        {limits.map(limit => (
                           <ListRow key={limit.id} style={{ gridTemplateColumns: '180px 180px 180px 120px 180px 180px 100px', columnGap: '15px' }}>
                              <div className="text-sm text-[#6D6D72]">
                                 ₦{formatWithCommas(limit.dailyLimit)}
                              </div>
                              <div className="text-sm text-[#6D6D72]">
                                 ₦{formatWithCommas(limit.weeklyLimit)}
                              </div>
                              <div className="text-sm text-[#6D6D72]">
                                 ₦{formatWithCommas(limit.monthlyLimit)}
                              </div>
                              <div>
                                 <span className={`text-xs px-2 py-1 rounded ${limit.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                                    {limit.isActive ? 'Active' : 'Inactive'}
                                 </span>
                              </div>
                              <div className="text-sm text-[#6D6D72]">
                                 {new Date(limit.createdAt).toLocaleDateString('en-US', { 
                                    year: 'numeric', 
                                    month: 'short', 
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                 })}
                              </div>
                              <div className="text-sm text-[#6D6D72]">
                                 {new Date(limit.updatedAt).toLocaleDateString('en-US', { 
                                    year: 'numeric', 
                                    month: 'short', 
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                 })}
                              </div>
                              <div>
                                 <button
                                    onClick={() => handleEdit(limit)}
                                    className="text-[#AF7AD5] hover:text-[#9B5FC4] transition-colors"
                                    title="Edit"
                                 >
                                    <Icon icon="solar:pen-linear" width={20} />
                                 </button>
                              </div>
                           </ListRow>
                        ))}
                     </>
                  )}

                  {!loading && limits.length === 0 && (
                     <div className="text-center py-12 text-[#6D6D72]">
                        No withdrawal limits configured yet. Click "Set New Limit" to create one.
                     </div>
                  )}
               </div>
            </RFMain>
         </Content>
      </Dashboard>
   );
}
